import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { estimateNicheBreakdown, fmtMoney } from "@/lib/earnings";

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-black border border-zinc-700 p-3 rounded-sm font-mono text-xs">
      <div className="text-white font-bold mb-1">{d.fullName}</div>
      <div className="text-yellow-400">
        {fmtMoney(d.monetized)} <span className="text-zinc-500">monetized</span>
      </div>
      <div className="text-zinc-300">
        {fmtMoney(d.preYpp)} <span className="text-zinc-500">pre-YPP</span>
      </div>
      <div className="border-t border-zinc-800 mt-2 pt-2 text-white">
        {fmtMoney(d.total)} <span className="text-zinc-500">/ mo total</span>
      </div>
      <div className="text-zinc-500 mt-1">
        {d.monetizedCount} / {d.totalCount} channels monetized
      </div>
    </div>
  );
}

function truncate(s, n) {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export default function EarningsDistribution({ niches }) {
  const navigate = useNavigate();

  const data = useMemo(() => {
    return niches
      .map((n) => {
        const b = estimateNicheBreakdown(n);
        return {
          niche_id: n.niche_id,
          name: truncate(n.name, 18),
          fullName: n.name,
          ...b,
        };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [niches]);

  if (data.length === 0) return null;

  const totalAcross = data.reduce((s, d) => s + d.total, 0);
  const totalMonetizedChannels = data.reduce((s, d) => s + d.monetizedCount, 0);
  const totalChannels = data.reduce((s, d) => s + d.totalCount, 0);
  const top = data[0];

  return (
    <section
      data-testid="earnings-distribution"
      className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-10"
    >
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-2">
            // EARNINGS DISTRIBUTION
          </div>
          <h2 className="font-display text-2xl font-bold text-white leading-tight">
            Where the money is hiding
          </h2>
          <p className="text-zinc-400 text-sm mt-1 max-w-xl">
            Estimated monthly run-rate per niche. Yellow = revenue from
            already-monetized creators (≥1k subs). Dim = pre-YPP potential.
          </p>
        </div>
        <div className="flex gap-px bg-zinc-800 border border-zinc-800 rounded-sm overflow-hidden text-right">
          <div className="bg-black px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Top niche
            </div>
            <div className="font-mono text-yellow-400 font-bold text-sm mt-1 max-w-[180px] truncate">
              {top.fullName}
            </div>
            <div className="text-zinc-300 text-xs font-mono">
              {fmtMoney(top.total)}/mo
            </div>
          </div>
          <div className="bg-black px-4 py-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              All niches
            </div>
            <div className="font-mono text-yellow-400 font-bold text-base mt-1">
              {fmtMoney(totalAcross)}/mo
            </div>
            <div className="text-zinc-500 text-xs font-mono">
              {totalMonetizedChannels}/{totalChannels} channels monetized
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
            barCategoryGap={8}
          >
            <XAxis
              type="number"
              tickFormatter={(v) => fmtMoney(v)}
              tick={{ fill: "#71717a", fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
              axisLine={{ stroke: "#27272a" }}
              tickLine={{ stroke: "#27272a" }}
            />
            <YAxis
              dataKey="name"
              type="category"
              width={140}
              tick={{ fill: "#fafafa", fontSize: 12, fontFamily: "IBM Plex Sans, sans-serif" }}
              axisLine={{ stroke: "#27272a" }}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(250,204,21,0.05)" }}
            />
            <Bar
              dataKey="monetized"
              stackId="rev"
              fill="#facc15"
              onClick={(d) => navigate(`/niches/${d.niche_id}`)}
              style={{ cursor: "pointer" }}
            >
              {data.map((d) => (
                <Cell key={d.niche_id} fill="#facc15" />
              ))}
            </Bar>
            <Bar
              dataKey="preYpp"
              stackId="rev"
              fill="#52525b"
              onClick={(d) => navigate(`/niches/${d.niche_id}`)}
              style={{ cursor: "pointer" }}
            >
              {data.map((d) => (
                <Cell key={d.niche_id} fill="#52525b" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-4 font-mono text-[11px] text-zinc-500">
        <span className="flex items-center gap-2">
          <span className="h-2 w-3 bg-yellow-400 inline-block" />
          Monetized (≥1k subs)
        </span>
        <span className="flex items-center gap-2">
          <span className="h-2 w-3 bg-zinc-600 inline-block" />
          Pre-YPP potential
        </span>
        <span className="ml-auto text-zinc-600">Click any bar to open the niche</span>
      </div>
    </section>
  );
}
